<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import eventService from '../services/event.service'


const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const cycleEvents = ref<any[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Pagination
const currentPage = ref(1)
const perPage = ref(25)
const totalCycles = ref(0)
const lastPage = ref(1)
const fromItem = computed(() => totalCycles.value > 0 ? (currentPage.value - 1) * perPage.value + 1 : 0)
const toItem = computed(() => Math.min(currentPage.value * perPage.value, totalCycles.value))

// Delete modal
const showDeleteModal = ref(false)
const cycleToDelete = ref<any>(null)
const deleting = ref(false)

// Dropdowns
const openDropdownId = ref<string | null>(null)

// Accordions (mobile)
const openAccordionIds = ref<Set<number>>(new Set())

// Get club UUID from init data
const clubUuid = computed(() => authStore.initData?.club?.uuid || null)

// Check permissions
const hasPermission = (permission: string) => {
  const permissions = authStore.initData?.permission_model || []
  return permissions.includes(permission)
}

const canCreate = computed(() => hasPermission('EVENT_CREATE'))
const canCycleShow = computed(() => hasPermission('EVENT_CYCLE_SHOW'))
const canCycleUpdate = computed(() => hasPermission('EVENT_CYCLE_UPDATE'))
const canCycleDelete = computed(() => hasPermission('EVENT_CYCLE_DELETE'))
const canSeeActions = computed(() => canCycleShow.value || canCycleUpdate.value || canCycleDelete.value)

// Fetch cycle events
const fetchCycleEvents = async (page: number = 1) => {
  if (!clubUuid.value) {
    error.value = t('eventCycles.errorNoClub')
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await eventService.getCycleEvents(
      clubUuid.value,
      perPage.value,
      page
    )
    cycleEvents.value = response.data || []
    totalCycles.value = response.total || 0
    currentPage.value = response.current_page || page
    lastPage.value = response.last_page || 1
  } catch (err: any) {
    console.error('Error fetching cycle events:', err)
    error.value = err.response?.data?.message || t('eventCycles.errorFetchingCycles')
  } finally {
    loading.value = false
  }
}

// Pagination handlers
const changePage = (page: number) => {
  currentPage.value = page
  fetchCycleEvents(page)
}

const prevPage = () => {
  if (currentPage.value > 1) {
    changePage(currentPage.value - 1)
  }
}

const nextPage = () => {
  if (currentPage.value < lastPage.value) {
    changePage(currentPage.value + 1)
  }
}

const changePerPage = () => {
  currentPage.value = 1
  fetchCycleEvents(1)
}

// Calculate visible page numbers
const visiblePages = computed(() => {
  const maxVisible = 5
  const pages: number[] = []

  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(lastPage.value, start + maxVisible - 1)

  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
})

// Delete handlers
const openDeleteModalFn = (cycle: any) => {
  cycleToDelete.value = cycle
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  cycleToDelete.value = null
}

const handleDelete = async () => {
  if (!cycleToDelete.value || !clubUuid.value) return

  deleting.value = true

  try {
    const response = await eventService.deleteCycle(clubUuid.value, cycleToDelete.value.uuid)
    const message = response?.message || t('eventCycles.cycleDeleted')
    toastStore.success(message)

    closeDeleteModal()
    fetchCycleEvents(currentPage.value)
  } catch (err: any) {
    console.error('Error deleting cycle:', err)
    const errorMessage = err.response?.data?.message || t('eventCycles.errorDeletingCycle')
    toastStore.error(errorMessage)
  } finally {
    deleting.value = false
  }
}

// Dropdown toggle
const toggleDropdown = (dropdownId: string, event: Event) => {
  event.stopPropagation()

  if (openDropdownId.value === dropdownId) {
    openDropdownId.value = null
  } else {
    openDropdownId.value = dropdownId
  }
}

const closeDropdowns = () => {
  openDropdownId.value = null
}

// Check if cycle is in last three (for dropdown positioning)
const isLastThree = (index: number) => {
  return index >= cycleEvents.value.length - 3
}

// Toggle accordion for mobile
const toggleAccordion = (index: number) => {
  if (openAccordionIds.value.has(index)) {
    openAccordionIds.value.delete(index)
  } else {
    openAccordionIds.value.add(index)
  }
}

// Close dropdowns when clicking outside
if (typeof window !== 'undefined') {
  document.addEventListener('click', closeDropdowns)
}

onMounted(() => {
  fetchCycleEvents()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Back to Events List -->
      <div class="mb-4">
        <router-link
          to="/events"
          class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('eventCycles.backToEventsList') }}
        </router-link>
      </div>

      <!-- Page Header -->
      <div class="mb-4 md:mb-4 mb-2 flex justify-between items-center">
        <h1 class="text-2xl font-bold text-gray-900">
          {{ t('eventCycles.title') }}
        </h1>
        <div class="flex items-center gap-2">
          <router-link
            v-if="canCreate"
            to="/events/create"
            class="px-4 py-2 md:px-4 md:py-2 px-2 py-1 bg-green-600 text-sm md:text-sm text-xs font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 flex items-center gap-2 md:gap-2 gap-1"
          >
            <svg class="w-4 h-4 md:w-4 md:h-4 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
            </svg>
            <span class="md:inline hidden">{{ t('events.addEvent') }}</span>
            <span class="md:hidden inline">{{ t('common.add') }}</span>
          </router-link>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-4 p-4 bg-red-50 border-2 border-red-300 text-red-700 rounded">
        {{ error }}
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('eventCycles.loadingCycles') }}</p>
      </div>

      <!-- Cycle Events Table (Desktop) -->
      <div v-if="!loading" class="hidden md:block bg-white border-2 border-gray-300 rounded shadow-sm overflow-y-visible" style="overflow-x: clip;">
        <table class="w-full text-sm">
          <thead class="bg-gray-100 border-b-2 border-gray-300">
            <tr>
              <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('eventCycles.event') }}</th>
              <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('eventCycles.place') }}</th>
              <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('eventCycles.cycleDate') }}</th>
              <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('eventCycles.cycleEndDate') }}</th>
              <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('eventCycles.startTime') }}</th>
              <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('eventCycles.endTime') }}</th>
              <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('eventCycles.frequency') }}</th>
              <th v-if="canSeeActions" class="px-4 py-3 text-right font-semibold text-gray-700 sticky right-0 bg-gray-100"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-for="(cycle, index) in cycleEvents" :key="index" class="hover:bg-gray-50">
              <td class="px-4 py-3 text-gray-900 font-medium">{{ cycle.title }}</td>
              <td class="px-4 py-3 text-gray-700">{{ cycle.place }}</td>
              <td class="px-4 py-3 text-gray-700">{{ cycle.start_date }}</td>
              <td class="px-4 py-3 text-gray-700">{{ cycle.cycle_end_date }}</td>
              <td class="px-4 py-3 text-gray-700">{{ cycle.start_time }}</td>
              <td class="px-4 py-3 text-gray-700">{{ cycle.end_time }}</td>
              <td class="px-4 py-3 text-gray-700">{{ cycle.frequency }}</td>
              <td v-if="canSeeActions" class="px-4 py-3 sticky right-0 bg-inherit text-right" style="position: relative;">
                <div class="flex items-center gap-1 justify-end">
                  <button
                    @click="toggleDropdown(`desktop-${index}`, $event)"
                    class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                  >
                    {{ t('common.actions') }}
                  </button>
                  <div
                    :class="[
                      'custom-dropdown',
                      'dropdown-bottom',
                      { 'show': openDropdownId === `desktop-${index}` }
                    ]"
                  >
                    <router-link v-if="canCycleUpdate" :to="`/events/${cycle.uuid}/cycle/edit`">{{ t('common.edit') }}</router-link>
                    <button v-if="canCycleDelete" class="delete-action" @click="openDeleteModalFn(cycle)">{{ t('common.delete') }}</button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Cycle Events Accordion (Mobile) -->
      <div v-if="!loading" class="md:hidden space-y-2">
        <div v-for="(cycle, index) in cycleEvents" :key="index" class="bg-white border-2 border-gray-300 rounded shadow-sm">
          <!-- Accordion Header -->
          <div class="flex items-center justify-between p-3 cursor-pointer" @click="toggleAccordion(index)">
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-900">{{ cycle.title }}</div>
              <div class="text-xs text-gray-600 mt-1">
                <span>{{ cycle.start_date }}</span>
                <span class="ml-2">{{ cycle.start_time }}</span>
              </div>
            </div>
            <div class="flex items-center gap-2 ml-2">
              <div v-if="canSeeActions" class="relative">
                <button
                  @click="toggleDropdown(`mobile-${index}`, $event)"
                  class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                >
                  {{ t('common.actions') }}
                </button>
                <div
                  :class="['custom-dropdown', 'dropdown-bottom', { 'show': openDropdownId === `mobile-${index}` }]"
                >
                  <router-link v-if="canCycleUpdate" :to="`/events/${cycle.uuid}/cycle/edit`">{{ t('common.edit') }}</router-link>
                  <button v-if="canCycleDelete" class="delete-action" @click="openDeleteModalFn(cycle)">{{ t('common.delete') }}</button>
                </div>
              </div>
              <svg
                :class="['w-5 h-5 text-gray-600 transition-transform', { 'rotate-180': openAccordionIds.has(index) }]"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </div>
          </div>
          <!-- Accordion Content -->
          <div v-show="openAccordionIds.has(index)" class="border-t-2 border-gray-300 p-3">
            <div class="space-y-2 text-xs">
              <div>
                <span class="font-semibold text-gray-700">{{ t('eventCycles.place') }}:</span>
                <span class="text-gray-600">{{ cycle.place }}</span>
              </div>
              <div>
                <span class="font-semibold text-gray-700">{{ t('eventCycles.cycleEndDate') }}:</span>
                <span class="text-gray-600">{{ cycle.cycle_end_date }}</span>
              </div>
              <div>
                <span class="font-semibold text-gray-700">{{ t('eventCycles.endTime') }}:</span>
                <span class="text-gray-600">{{ cycle.end_time }}</span>
              </div>
              <div>
                <span class="font-semibold text-gray-700">{{ t('eventCycles.frequency') }}:</span>
                <span class="text-gray-600">{{ cycle.frequency }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && totalCycles > 0" class="mt-4 flex flex-col lg:flex-row justify-between items-center gap-4">
        <div class="text-sm text-gray-700 w-full lg:w-auto text-center lg:text-left">
          {{ t('eventCycles.displaying') }} {{ fromItem }} - {{ toItem }} {{ t('eventCycles.of') }} {{ totalCycles }} {{ t('eventCycles.cyclesWord') }}
        </div>
        <div class="flex flex-col md:flex-row items-center gap-4 w-full lg:w-auto">
          <div class="flex items-center gap-2 w-full md:w-auto justify-center md:justify-start">
            <label for="perPage" class="text-sm text-gray-700">{{ t('eventCycles.cyclesPerPage') }}:</label>
            <select id="perPage" v-model.number="perPage" @change="changePerPage" class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="75">75</option>
              <option :value="100">100</option>
            </select>
          </div>
          <div class="flex items-center gap-1 w-full md:w-auto justify-center md:justify-start">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="changePage(page)"
              :class="[
                'px-3 py-1 border-2 rounded text-sm',
                page === currentPage
                  ? 'border-gray-900 bg-gray-900 text-white font-medium'
                  : 'border-gray-300 bg-white hover:bg-gray-50'
              ]"
            >
              {{ page }}
            </button>
            <button
              @click="nextPage"
              :disabled="currentPage === lastPage"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>

  <!-- Delete Confirmation Modal -->
  <div
    v-if="showDeleteModal"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeDeleteModal"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('eventCycles.deleteConfirmation') }}</h2>
        <p class="text-gray-700 mb-6">
          {{ t('eventCycles.deleteConfirmationMessage') }} "<span class="font-semibold">{{ cycleToDelete?.title }}</span>"?
        </p>

        <div class="flex gap-3 justify-end">
          <button
            @click="closeDeleteModal"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            :disabled="deleting"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            @click="handleDelete"
            class="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="deleting"
          >
            {{ deleting ? t('eventCycles.deleting') : t('common.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>

</template>

<style scoped>
</style>
